'use client';

import { useParams } from 'next/navigation';

// Import translations
import type { Translations } from '@/types/translations';
import en from '@/locales/en';
import es from '@/locales/es';
import pt from '@/locales/pt';
import ca from '@/locales/ca';
import eu from '@/locales/eu';
import fr from '@/locales/fr';
import it from '@/locales/it';

const translations: Record<string, Translations> = { en, es, pt, ca, eu, fr, it } as any;

type Language = keyof typeof translations;
type DeepKeys<T> = T extends object
  ? {
    [K in keyof T]: K extends string
    ? T[K] extends object
    ? `${K}.${DeepKeys<T[K]>}`
    : K
    : never;
  }[keyof T]
  : never;

export type TranslationKey = string;

export function useTranslation() {
  const { lang } = useParams();
  const language: Language =
    typeof lang === 'string' && lang in translations ? (lang as Language) : 'en';

  const t = (key: TranslationKey): string => {
    const keys = key.split('.');
    let translation: unknown = translations[language];

    if (!translation) return key;

    for (const k of keys) {
      translation = (translation as Record<string, unknown>)?.[k];
      if (!translation) return key;
    }

    return translation as string;
  };

  return { t, lang: language };
} 